import React, { useCallback } from 'react';

import { find } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Modal } from '~/components';

import { GroupComponent } from './components';

const ModalAccordion = React.forwardRef((props, ref) => {
  const {
    title,
    tasks,
    withoutGuides,
    onClose,
    submitForm,
    loading,
    buttons,
  } = props;

  const [accordion, setAccordion] = React.useState(false);
  const [initial, setInitial] = React.useState({});

  const modalRef = React.useRef(null);
  const groupRef = React.useRef(null);

  // Width window
  const isMobile = window.innerWidth <= 425;

  // Open modal
  const openModal = useCallback(() => {
    modalRef.current.handleOpenModal();
  }, []);

  // Close modal
  const closeModal = useCallback(() => {
    modalRef.current.handleCloseModal();
  }, []);

  // Save and continue form
  const saveForm = useCallback(() => {
    groupRef.current.submitForm();
  }, []);

  // Close modal
  const handleOnClose = useCallback(() => {
    modalRef.current.handleCloseModal();
    groupRef.current.handleResetData();
    setInitial({});
    onClose();
  }, []);

  // Validate form
  const schemaValidate = React.useMemo(() => {
    if (accordion) {
      const task = find(tasks, (ta) => ta.slug === accordion);
      return task.schema;
    }

    return [...tasks].shift().schema;
  }, [accordion]);

  // Submit form
  const handleSubmit = React.useCallback(
    async (data) => {
      try {
        const schema = Yup.object().shape(schemaValidate);

        await schema.validate(data, { abortEarly: false });
        await submitForm(data);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = {};

          err.inner.forEach((error) => {
            errors[error.path] = error.message.replace(`${error.path} `, '');
          });

          groupRef.current.setErrors(errors);
        }
      }
    },
    [schemaValidate]
  );

  // Save data form
  const handleOnSave = React.useCallback((values) => {
    setInitial(values);
  }, []);

  // Change accordion
  const handleChangeAccordion = React.useCallback((value) => {
    setAccordion(value);
  }, []);

  // Accordion initial
  React.useEffect(() => {
    if (!accordion) {
      const task = [...tasks].shift().slug.toString();
      handleChangeAccordion(task);
    }
  }, []);

  // Export functions
  React.useImperativeHandle(ref, () => ({
    openModal,
    closeModal,
    handleOnClose,
    saveForm,
  }));

  return (
    <Modal
      ref={modalRef}
      title={title}
      actions={buttons || []}
      loading={loading}
      maxWidth="xl"
      component={() => (
        <GroupComponent
          ref={groupRef}
          taks={tasks}
          withoutGuides={withoutGuides}
          toggleAccordion={handleChangeAccordion}
          onSubmitForm={(values) => handleSubmit(values)}
          onSave={handleOnSave}
          initials={initial}
          accordion={accordion}
        />
      )}
      fullScreen={isMobile}
    />
  );
});

ModalAccordion.propTypes = {
  title: PropTypes.string,
  tasks: PropTypes.arrayOf(
    PropTypes.objectOf(
      PropTypes.shape({
        slug: PropTypes.string,
        title: PropTypes.string,
        resume: PropTypes.string,
        description: PropTypes.string,
        component: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
        schema: PropTypes.objectOf(PropTypes.any),
      })
    )
  ).isRequired,
  withoutGuides: PropTypes.bool,
  onClose: PropTypes.func,
  submitForm: PropTypes.func,
  loading: PropTypes.bool,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      callback: PropTypes.func,
      color: PropTypes.string,
      autoFocus: PropTypes.bool,
      disabled: PropTypes.bool,
      variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
      startIcon: PropTypes.node,
      endIcon: PropTypes.node,
    })
  ),
};

ModalAccordion.defaultProps = {
  title: '',
  withoutGuides: false,
  onClose: () => undefined,
  submitForm: (values) => values,
  loading: false,
  buttons: [],
};

export default ModalAccordion;
